import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
} from "@material-ui/core";
import { DesignTemplate } from "../useDesignManagement";
import {
  industryOptions,
  useCaseOptions,
  promoOfferTypeOptions,
  promoFrequencyOptions,
  dollarAmountOptions,
  percentageAmountOptions,
} from "../../creativeConstants";
import SelectMenu from "../../../flyer/builder/components/Inspector/components/SelectMenu";

interface TemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSaveTemplate: (input: {
    name: string;
    description?: string;
    isPublic: boolean;
    promoOffer?: string[];
    industry?: string[];
    useCase?: string[];
    uspsCompliant?: boolean;
    thumbnail?: string;
  }) => Promise<void>;
  onApplyTemplate: (template: DesignTemplate, designId: string) => Promise<void>;
  onDeleteTemplate: (templateId: string) => Promise<void>;
  loading?: boolean;
  currentDesignId: string;
}

interface PromoSelection {
  offerType: string;
  frequency: string;
  amount: string;
}

const TemplateModal: React.FC<TemplateModalProps> = ({ open, onClose, onSaveTemplate, loading = false }) => {
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [promoSelection, setPromoSelection] = useState<PromoSelection>({ offerType: "", frequency: "", amount: "" });
  const [industry, setIndustry] = useState<string[]>([]);
  const [useCase, setUseCase] = useState<string[]>([]);
  const [uspsCompliant, setUspsCompliant] = useState(false);

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;

    setSaving(true);
    try {
      // Convert promoSelection to the expected format
      const promoOffer =
        promoSelection.offerType && promoSelection.frequency && promoSelection.amount
          ? [`${promoSelection.offerType}-${promoSelection.frequency}-${promoSelection.amount}`]
          : [];

      await onSaveTemplate({
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        isPublic,
        promoOffer: promoOffer.length > 0 ? promoOffer : undefined,
        industry: industry.length > 0 ? industry : undefined,
        useCase: useCase.length > 0 ? useCase : undefined,
        uspsCompliant,
      });
      // Clear form fields
      setTemplateName("");
      setTemplateDescription("");
      setIsPublic(false);
      setTitle("");
      setPromoSelection({ offerType: "", frequency: "", amount: "" });
      setIndustry([]);
      setUseCase([]);
      setUspsCompliant(false);

      // Close modal after successful save
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
    } finally {
      setSaving(false);
    }
  };

  const createSelectMenuItems = (
    options: { value: string; label: string }[],
    selectedValues: string[],
    onSelect: (values: string[]) => void
  ) => {
    return options.map((option) => ({
      label: option.label,
      action: () => {
        const isSelected = selectedValues.includes(option.value);
        let newValues: string[];

        if (isSelected) {
          newValues = selectedValues.filter((value) => value !== option.value);
        } else {
          newValues = [...selectedValues, option.value];
        }

        onSelect(newValues);
      },
      isChecked: selectedValues.includes(option.value),
    }));
  };

  const createSingleSelectMenuItems = (
    options: { value: string; label: string }[],
    selectedValue: string,
    onSelect: (value: string) => void
  ) => {
    return options.map((option) => ({
      label: option.label,
      action: () => onSelect(option.value),
      isChecked: selectedValue === option.value,
    }));
  };

  const getDisplayValue = (selectedValues: string[], options: { value: string; label: string }[]) => {
    if (selectedValues.length === 0) {
      return "Select options...";
    }

    const selectedLabels = selectedValues
      .map((value) => options.find((opt) => opt.value === value)?.label)
      .filter(Boolean);

    if (selectedLabels.length <= 2) {
      return selectedLabels.join(", ");
    }

    return `${selectedLabels.length} options selected`;
  };

  const getSingleDisplayValue = (selectedValue: string, options: { value: string; label: string }[]) => {
    if (!selectedValue) {
      return "Select option...";
    }
    return options.find((opt) => opt.value === selectedValue)?.label || selectedValue;
  };

  // Promo selection handlers
  const updatePromoSelection = (field: keyof PromoSelection, value: string) => {
    const newSelection = { ...promoSelection, [field]: value };

    // Reset dependent fields when offer type changes
    if (field === "offerType") {
      newSelection.frequency = "";
      newSelection.amount = "";
    } else if (field === "frequency") {
      newSelection.amount = "";
    }

    setPromoSelection(newSelection);
  };

  const industryItems = createSelectMenuItems(
    industryOptions.filter((opt) => opt.value !== "All"),
    industry,
    setIndustry
  );

  const useCaseItems = createSelectMenuItems(useCaseOptions, useCase, setUseCase);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <div>
          <Typography variant="h6" gutterBottom>
            Save Current Design as Template
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "var(--color-deep-blue)",
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                Template Name
              </label>
              <input
                type="text"
                placeholder="Enter Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="w-full self-stretch h-10 max-h-10 min-h-10 px-2 py-1 bg-white rounded-lg shadow-[0px_2px_8px_0px_rgba(39,55,84,0.06)] outline outline-1 outline-offset-[-1px] outline-Colors-Neutral-50 inline-flex justify-start items-center gap-2"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "var(--color-deep-blue)",
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                Description
              </label>
              <input
                type="text"
                placeholder="Enter Description (optional)"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                className="w-full self-stretch h-10 max-h-10 min-h-10 px-2 py-1 bg-white rounded-lg shadow-[0px_2px_8px_0px_rgba(39,55,84,0.06)] outline outline-1 outline-offset-[-1px] outline-Colors-Neutral-50 inline-flex justify-start items-center gap-2"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "var(--color-deep-blue)",
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                Title
              </label>
              <input
                type="text"
                placeholder="Enter Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full self-stretch h-10 max-h-10 min-h-10 px-2 py-1 bg-white rounded-lg shadow-[0px_2px_8px_0px_rgba(39,55,84,0.06)] outline outline-1 outline-offset-[-1px] outline-Colors-Neutral-50 inline-flex justify-start items-center gap-2"
                required
              />
            </Grid>

            {/* Promo Offer Section */}
            <Grid item xs={12}>
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "var(--color-deep-blue)",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Promo Offer
                </label>

                <div style={{ padding: "12px", border: "1px solid #E2E2FC", borderRadius: "8px" }}>
                  {/* Step 1: Offer Type */}
                  <div style={{ marginBottom: "12px" }}>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "var(--color-deep-blue)",
                        marginBottom: "4px",
                        display: "block",
                      }}
                    >
                      Offer Type
                    </label>
                    <SelectMenu
                      isDisabled={false}
                      items={createSingleSelectMenuItems(promoOfferTypeOptions, promoSelection.offerType, (value) =>
                        updatePromoSelection("offerType", value)
                      )}
                      displayValue={getSingleDisplayValue(promoSelection.offerType, promoOfferTypeOptions)}
                      cypressId="promoOfferType"
                    />
                  </div>

                  {/* Step 2: Frequency (only show if offer type is selected) */}
                  {promoSelection.offerType && (
                    <div style={{ marginBottom: "12px" }}>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "var(--color-deep-blue)",
                          marginBottom: "4px",
                          display: "block",
                        }}
                      >
                        Frequency
                      </label>
                      <SelectMenu
                        isDisabled={false}
                        items={createSingleSelectMenuItems(promoFrequencyOptions, promoSelection.frequency, (value) =>
                          updatePromoSelection("frequency", value)
                        )}
                        displayValue={getSingleDisplayValue(promoSelection.frequency, promoFrequencyOptions)}
                        cypressId="promoFrequency"
                      />
                    </div>
                  )}

                  {/* Step 3: Amount (only show if frequency is selected) */}
                  {promoSelection.frequency && (
                    <div style={{ marginBottom: "12px" }}>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "var(--color-deep-blue)",
                          marginBottom: "4px",
                          display: "block",
                        }}
                      >
                        Amount
                      </label>
                      <SelectMenu
                        isDisabled={false}
                        items={createSingleSelectMenuItems(
                          promoSelection.offerType === "dollar" ? dollarAmountOptions : percentageAmountOptions,
                          promoSelection.amount,
                          (value) => updatePromoSelection("amount", value)
                        )}
                        displayValue={getSingleDisplayValue(
                          promoSelection.amount,
                          promoSelection.offerType === "dollar" ? dollarAmountOptions : percentageAmountOptions
                        )}
                        cypressId="promoAmount"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Grid>

            <Grid item xs={12}>
              <div style={{ marginBottom: "8px" }}>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "var(--color-deep-blue)",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Industry
                </label>
                <SelectMenu
                  isDisabled={false}
                  items={industryItems}
                  displayValue={getDisplayValue(
                    industry,
                    industryOptions.filter((opt) => opt.value !== "All")
                  )}
                  cypressId="industry"
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <div style={{ marginBottom: "8px" }}>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "var(--color-deep-blue)",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Use Case
                </label>
                <SelectMenu
                  isDisabled={false}
                  items={useCaseItems}
                  displayValue={getDisplayValue(useCase, useCaseOptions)}
                  cypressId="useCase"
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} color="primary" />
                }
                label="Make this template public (available to all users)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={uspsCompliant}
                    onChange={(e) => setUspsCompliant(e.target.checked)}
                    color="primary"
                  />
                }
                label="USPS compliant"
              />
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        <>
          <Button data-cy="templateModalCancelButton" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-cy="templateModalSaveButton"
            onClick={handleSaveTemplate}
            color="primary"
            variant="contained"
            disabled={!templateName.trim() || saving || loading}
          >
            {saving ? "Saving..." : "Save Template"}
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateModal;
